import { useQuery } from "@apollo/client";
import { HOLIDAYS } from "../../api/query";
import Loader from "../../components/common/Loader";
import { format } from "date-fns";
import backArrow from "../../assets/task/back_arrow.svg";
import { useNavigate } from "react-router-dom";
import BreadCumb from "../../components/common/Breadcrumb";

const Holidays = () => {
  const navigate = useNavigate();

  // Get current year
  const currentYear = new Date().getFullYear();

  // Set start date as January 1 T00:00:00Z
  const startDate = `${currentYear}-01-01T00:00:00Z`;

  // Set end date as December 31 T23:59:59Z
  const endDate = `${currentYear}-12-31T23:59:59Z`;

  // API call
  const { loading, data, error } = useQuery(HOLIDAYS, {
    variables: {
      start_date: startDate,
      end_date: endDate,
    },
    fetchPolicy: "no-cache",
  });

  const holidays = data?.holidays?.holidays || [];

  // Function to get subtle color based on index
  const getSubtleColor = (index: number) => {
    const colors = [
      { bg: "bg-blue-50 dark:bg-blue-900/10", text: "text-blue-700 dark:text-blue-300", border: "border-blue-200 dark:border-blue-800/30" },
      { bg: "bg-purple-50 dark:bg-purple-900/10", text: "text-purple-700 dark:text-purple-300", border: "border-purple-200 dark:border-purple-800/30" },
      { bg: "bg-emerald-50 dark:bg-emerald-900/10", text: "text-emerald-700 dark:text-emerald-300", border: "border-emerald-200 dark:border-emerald-800/30" },
      { bg: "bg-indigo-50 dark:bg-indigo-900/10", text: "text-indigo-700 dark:text-indigo-300", border: "border-indigo-200 dark:border-indigo-800/30" },
      { bg: "bg-teal-50 dark:bg-teal-900/10", text: "text-teal-700 dark:text-teal-300", border: "border-teal-200 dark:border-teal-800/30" },
      { bg: "bg-cyan-50 dark:bg-cyan-900/10", text: "text-cyan-700 dark:text-cyan-300", border: "border-cyan-200 dark:border-cyan-800/30" },
    ];
    return colors[index % colors.length];
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="w-full relative overflow-hidden flex flex-col items-start justify-start min-w-[360px] max-w-[100%] leading-[normal] tracking-[normal] text-left text-xl text-colors-text-black font-body-xs-semi-bold mq450:max-w-full mq950:max-w-full mq1425:max-w-full mq1900:max-w-full">
      <div className="self-stretch bg-gray overflow-hidden flex flex-col items-center justify-start relative gap-[24px] min-h-[913px] max-w-full">
        <div
          id="cts-respon-p-dv"
          className="w-full flex flex-col items-start justify-start py-40 px-20 mq1490:px-[30px] mq575:px-5 box-border gap-[32px] max-w-[100%] mq1425:max-w-full"
        >
          {/* Header */}
         <div className="grid gap-4 w-full">
           <BreadCumb />
          <div className="self-stretch flex flex-row items-center justify-between gap-[16px] max-w-full mq575:flex-col mq575:items-start">
             
            <div className="flex flex-row items-center gap-[12px]">
              <img
                className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img hover:opacity-70 transition-opacity"
                alt=""
                src={backArrow}
                onClick={() => navigate("/dashboard")}
              />
              <h3 className="m-0 relative text-2xl font-semibold font-inherit mq450:text-xl dark:text-white">
                Holidays {currentYear}
              </h3>
            </div>
            <div className="flex items-center gap-2 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 text-slate-700 dark:text-slate-200 px-4 py-2 rounded-lg border border-blue-100 dark:border-blue-800/30">
              <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-sm font-medium">{holidays.length} Total</span>
            </div>
          </div>
         </div>

          {/* Holidays List */}
          <div className="w-[100%] flex flex-col items-start justify-start gap-[20px] max-w-[100%] mq1425:max-w-full">
            {error && (
              <div className="self-stretch rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30 flex flex-col items-center justify-center p-8 box-border">
                <p className="text-red-600 dark:text-red-400 text-sm">
                  Error loading holidays: {error.message}
                </p>
              </div>
            )}

            {!loading && holidays.length === 0 && (
              <div className="self-stretch rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 flex flex-col items-center justify-center p-12 box-border">
                <svg className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-slate-500 dark:text-slate-400 text-sm">No holidays found for {currentYear}</p>
              </div>
            )}

            {holidays.length > 0 && (
              <div className="self-stretch rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm">
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-gradient-to-r from-slate-50 via-blue-50/30 to-purple-50/30 dark:from-slate-900/50 dark:via-blue-900/10 dark:to-purple-900/10 border-b border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  <div className="col-span-1 mq950:col-span-2">Date</div>
                  <div className="col-span-5 mq950:col-span-6">Holiday Name</div>
                  <div className="col-span-3 mq950:hidden">Day</div>
                  <div className="col-span-3 mq950:col-span-4">Type</div>
                </div>

                {/* Table Body */}
                <div className="divide-y divide-slate-100 dark:divide-slate-700/50">
                  {holidays.map((holiday: any, index: number) => {
                    const holidayDate = holiday.date ? new Date(holiday.date) : null;
                    // Calculate day of the week from the date
                    const dayOfWeek = holidayDate ? format(holidayDate, "EEEE") : "N/A";
                    const isEven = index % 2 === 0;
                    const colors = getSubtleColor(index);

                    return (
                      <div
                        key={holiday.id}
                        className={`grid grid-cols-12 gap-4 px-6 py-5 transition-all duration-200 hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-purple-50/50 dark:hover:from-blue-900/10 dark:hover:to-purple-900/10 ${isEven ? "" : "bg-slate-50/50 dark:bg-slate-800/30"
                          }`}
                      >
                        {/* Date */}
                        <div className="col-span-1 mq950:col-span-2 flex flex-col items-start">
                          <div className={`flex flex-col items-center justify-center px-3 py-2 rounded-lg ${colors.bg} ${colors.border} border min-w-[60px]`}>
                            <span className={`text-xs font-medium ${colors.text} uppercase`}>
                              {holidayDate ? format(holidayDate, "MMM") : ""}
                            </span>
                            <span className={`text-xl font-bold ${colors.text}`}>
                              {holidayDate ? format(holidayDate, "dd") : ""}
                            </span>
                          </div>
                        </div>

                        {/* Holiday Name */}
                        <div className="col-span-5 mq950:col-span-6 flex flex-col justify-center">
                          <h4 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-1">
                            {holiday.name}
                          </h4>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {holidayDate ? format(holidayDate, "MMMM dd, yyyy") : "N/A"}
                          </p>
                        </div>

                        {/* Day */}
                        <div className="col-span-3 mq950:hidden flex items-center">
                          <span className={`text-sm font-medium ${colors.text}`}>
                            {dayOfWeek}
                          </span>
                        </div>

                        {/* Type */}
                        <div className="col-span-3 mq950:col-span-4 flex items-center">
                          {holiday.type ? (
                            <span className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium ${colors.bg} ${colors.text} ${colors.border} border`}>
                              {holiday.type}
                            </span>
                          ) : (
                            <span className="text-sm text-slate-400 dark:text-slate-500">—</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Holidays;
